/* eslint-disable no-console */
/**
 * useSlotSelection Hook
 *
 * Manages the slot selection flow when user drops an image without context.
 * Handles detecting dimensions, suggesting best slot, and completing selection.
 *
 * Unix Philosophy: One hook does one thing well - slot selection flow.
 */

import { useState, useCallback } from 'react';

import type { SlotType, SlotSuggestion, ImageDimensions } from '../../components/global/SolomonPanel/types';

export interface SlotSelectionState {
  isActive: boolean;
  pendingImage: File | null;
  suggestion: SlotSuggestion | null;
  dimensions: ImageDimensions | null;
}

interface UseSlotSelectionOptions {
  /** Callback when slot selection is completed - receives the file for upload */
  onComplete?: (slot: SlotType, file: File) => void;
}

interface UseSlotSelectionReturn {
  /** Current slot selection state */
  slotSelection: SlotSelectionState;
  /** Start the slot selection flow with a dropped image */
  startSlotSelection: (file: File, dimensions: ImageDimensions, suggestion: SlotSuggestion) => void;
  /** Complete slot selection with the chosen slot */
  completeSlotSelection: (slot: SlotType) => void;
  /** Cancel slot selection */
  cancelSlotSelection: () => void;
}

const INITIAL_STATE: SlotSelectionState = {
  isActive: false,
  pendingImage: null,
  suggestion: null,
  dimensions: null,
};

export function useSlotSelection(options: UseSlotSelectionOptions = {}): UseSlotSelectionReturn {
  const { onComplete } = options;
  const [slotSelection, setSlotSelection] = useState<SlotSelectionState>(INITIAL_STATE);

  const startSlotSelection = useCallback((file: File, dimensions: ImageDimensions, suggestion: SlotSuggestion) => {
    console.log('[useSlotSelection] startSlotSelection called', {
      fileName: file.name,
      suggestion: suggestion.slot,
    });
    setSlotSelection({
      isActive: true,
      pendingImage: file,
      suggestion,
      dimensions,
    });
  }, []);

  const completeSlotSelection = useCallback(
    (slot: SlotType) => {
      console.log('[useSlotSelection] completeSlotSelection called', { slot });
      if (slotSelection.pendingImage) {
        // Pass the file to the callback for upload
        // Caller is responsible for uploading and getting permanent URL
        onComplete?.(slot, slotSelection.pendingImage);
      }
      // Reset slot selection state
      setSlotSelection(INITIAL_STATE);
    },
    [slotSelection.pendingImage, onComplete]
  );

  const cancelSlotSelection = useCallback(() => {
    console.log('[useSlotSelection] cancelSlotSelection called');
    setSlotSelection(INITIAL_STATE);
  }, []);

  return {
    slotSelection,
    startSlotSelection,
    completeSlotSelection,
    cancelSlotSelection,
  };
}

export default useSlotSelection;
